import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";
import {
  AccessRole,
  createAccessRole,
  deleteAccessRole,
  listAccessRoles,
  setAccessRoleCameras,
} from "../../services/accessRolesService";
import { CameraOption, listCameraOptions } from "../../services/usersService";
import "./AccessRoles.css";

type Props = { user?: any; onSignOut?: () => void };

type PortalRole = "OWNER" | "INTEGRATOR" | "ADMIN" | "VIEWER";

function emailOf(user: any) {
  return (
    user?.signInDetails?.loginId ||
    user?.attributes?.email ||
    user?.username ||
    ""
  );
}

function roleLabel(role: PortalRole | null) {
  if (role === "OWNER") return "Owner";
  if (role === "INTEGRATOR") return "Integrator";
  if (role === "ADMIN") return "Admin";
  if (role === "VIEWER") return "Viewer";
  return "Account";
}

export default function AccessRoles({ user, onSignOut }: Props) {
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [cameras, setCameras] = useState<CameraOption[]>([]);
  const [callerRole, setCallerRole] = useState<PortalRole | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [cameraDraft, setCameraDraft] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [roleResult, cameraResult] = await Promise.all([
        listAccessRoles(),
        listCameraOptions(),
      ]);
      setRoles(Array.isArray(roleResult.items) ? roleResult.items : []);
      setCallerRole(roleResult.callerRole);
      setCameras(cameraResult);
      setSelectedRoleId((current) => {
        if (current && roleResult.items.some((role) => role.accessRoleId === current)) {
          return current;
        }
        return roleResult.items[0]?.accessRoleId || "";
      });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load Access Roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedRole = useMemo(
    () => roles.find((role) => role.accessRoleId === selectedRoleId) || null,
    [roles, selectedRoleId],
  );

  useEffect(() => {
    setCameraDraft(selectedRole?.cameraIds || []);
  }, [selectedRoleId, selectedRole?.cameraIds]);

  async function createRole(event: FormEvent) {
    event.preventDefault();
    if (callerRole !== "INTEGRATOR") return;
    setBusy("create");
    setError("");
    setMessage("");
    try {
      const created = await createAccessRole({ name: name.trim(), description: description.trim() });
      setName("");
      setDescription("");
      await load();
      setSelectedRoleId(created.accessRoleId);
      setMessage(`Access Role “${created.name}” created.`);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Unable to create Access Role.");
    } finally {
      setBusy("");
    }
  }

  async function saveCameras() {
    if ((callerRole !== "INTEGRATOR" && callerRole !== "OWNER") || !selectedRole) return;
    setBusy(`cameras:${selectedRole.accessRoleId}`);
    setError("");
    setMessage("");
    try {
      await setAccessRoleCameras(selectedRole.accessRoleId, cameraDraft);
      await load();
      setMessage(`Camera access updated for ${selectedRole.name}.`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update role cameras.");
    } finally {
      setBusy("");
    }
  }

  async function removeRole(role: AccessRole) {
    if (callerRole !== "INTEGRATOR") return;
    if (window.confirm && !window.confirm(`Delete Access Role “${role.name}”?`)) return;
    setBusy(`delete:${role.accessRoleId}`);
    setError("");
    setMessage("");
    try {
      await deleteAccessRole(role.accessRoleId);
      await load();
      setMessage("Access Role deleted.");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Unable to delete Access Role.");
    } finally {
      setBusy("");
    }
  }

  function toggleCamera(cameraId: string) {
    setCameraDraft((current) =>
      current.includes(cameraId)
        ? current.filter((id) => id !== cameraId)
        : [...current, cameraId],
    );
  }

  const canManageRoles = callerRole === "INTEGRATOR";
  const canManageCameras = callerRole === "INTEGRATOR" || callerRole === "OWNER";

  return (
    <div className="access-roles-page">
      <TopBar onSignOut={onSignOut} email={emailOf(user)} />
      <main className="access-roles-shell">
        <header className="access-roles-hero">
          <div>
            <div className="access-roles-eyebrow">Camera authorization</div>
            <h1>Access Roles</h1>
            <p>
              {callerRole === "OWNER"
                ? "Review Integrator camera groups and assign unassigned cameras, including Gateway cameras, to the correct Integrator Access Role."
                : callerRole === "INTEGRATOR"
                  ? "Create camera groups, place cameras into them, and assign those groups to Admin and Viewer accounts."
                  : "These are the camera groups your Integrator assigned to your account."}
            </p>
          </div>
          <span className="access-roles-badge">{roleLabel(callerRole)}</span>
        </header>

        {error && <div className="access-roles-alert access-roles-alert--error">{error}</div>}
        {message && <div className="access-roles-alert access-roles-alert--success">{message}</div>}

        {canManageRoles && (
          <section className="access-roles-panel">
            <div className="access-roles-heading">
              <div>
                <div className="access-roles-eyebrow">New camera group</div>
                <h2>Create Access Role</h2>
                <p>Cameras can belong to multiple Access Roles. Admins and Viewers can also be assigned multiple roles.</p>
              </div>
            </div>
            <form className="access-roles-create" onSubmit={createRole}>
              <label>
                <span>Name</span>
                <input required maxLength={100} value={name} onChange={(event) => setName(event.target.value)} placeholder="Warehouse" />
              </label>
              <label className="access-roles-create__wide">
                <span>Description</span>
                <input maxLength={500} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Warehouse cameras and loading area" />
              </label>
              <button type="submit" disabled={busy === "create"}>{busy === "create" ? "Creating…" : "Create role"}</button>
            </form>
          </section>
        )}

        <section className="access-roles-layout">
          <div className="access-roles-panel">
            <div className="access-roles-heading">
              <div>
                <div className="access-roles-eyebrow">Available roles</div>
                <h2>{callerRole === "OWNER" ? "Integrator Access Roles" : "Camera groups"}</h2>
              </div>
              <span>{roles.length}</span>
            </div>

            {loading ? (
              <div className="access-roles-empty">Loading Access Roles…</div>
            ) : roles.length === 0 ? (
              <div className="access-roles-empty">
                {canManageRoles ? "Create your first Access Role to begin assigning cameras." : "No Access Roles are assigned to this account."}
              </div>
            ) : (
              <div className="access-roles-list">
                {roles.map((role) => (
                  <button
                    type="button"
                    key={role.accessRoleId}
                    className={`access-role-card${selectedRoleId === role.accessRoleId ? " access-role-card--active" : ""}`}
                    onClick={() => setSelectedRoleId(role.accessRoleId)}
                  >
                    <div className="access-role-card__top">
                      <strong>{role.name}</strong>
                      <span>{role.cameraCount} cameras</span>
                    </div>
                    {callerRole === "OWNER" && role.integratorName && (
                      <div className="access-role-card__integrator">Integrator: {role.integratorName}</div>
                    )}
                    <p>{role.description || "No description"}</p>
                    <div className="access-role-card__meta">
                      <span>{role.userCount} users</span>
                      <span>{role.accessRoleId}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="access-roles-panel">
            {!selectedRole ? (
              <div className="access-roles-empty">Select an Access Role to view its cameras.</div>
            ) : (
              <>
                <div className="access-roles-heading">
                  <div>
                    <div className="access-roles-eyebrow">Camera membership</div>
                    <h2>{selectedRole.name}</h2>
                    <p>
                      {canManageCameras
                        ? "Select every camera that should belong to this Access Role. A camera can stay assigned to other Access Roles at the same time."
                        : "Cameras currently available through this Access Role."}
                    </p>
                  </div>
                  {canManageRoles && (
                    <button
                      type="button"
                      className="access-roles-danger"
                      disabled={Boolean(busy)}
                      onClick={() => void removeRole(selectedRole)}
                    >
                      Delete
                    </button>
                  )}
                </div>

                <div className="access-role-cameras">
                  {cameras.length === 0 ? (
                    <div className="access-roles-empty">No cameras are available in this scope.</div>
                  ) : (
                    cameras.map((camera) => {
                      const checked = canManageCameras
                        ? cameraDraft.includes(camera.cameraId)
                        : selectedRole.cameraIds.includes(camera.cameraId);
                      if (!canManageCameras && !checked) return null;
                      const otherRoleNames = roles
                        .filter(
                          (role) =>
                            role.accessRoleId !== selectedRole.accessRoleId &&
                            role.cameraIds.includes(camera.cameraId),
                        )
                        .map((role) => role.name);
                      return (
                        <label className="access-role-camera" key={camera.cameraId}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canManageCameras || Boolean(busy)}
                            onChange={() => toggleCamera(camera.cameraId)}
                          />
                          <div>
                            <strong>{camera.name}</strong>
                            <span>#{camera.cameraId}</span>
                          </div>
                          {otherRoleNames.length > 0 && (
                            <small>Also in: {otherRoleNames.join(", ")}</small>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>

                {canManageCameras && (
                  <div className="access-roles-actions">
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => void saveCameras()}
                    >
                      {busy.startsWith("cameras:") ? "Saving…" : "Save camera access"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}